import { useCallback } from "react";
import { FormattedFile } from "@/react/components/entityViews/files/context/EntityFileContext";
import { isImageFile } from "@/other/utils/utils.jsx";
import { prepareFileForSpotIncentive } from "@/react/components/entityViews/files/utils/spotIncentiveFileUtils";
import { toast } from "sonner";
import type { ISwivuProject } from "@/types/SwivuProject";
import type { EntityRecord } from "@/types/entity";
import { getEligibleResourceForSpotIncentive, openAdjustPointsEditorWithGuard } from "@/other/utils/incentiveUtils.jsx";
import { DateHelper } from "@bryntum/schedulerpro";
import { BoardFile } from "@/bryntum-lib/models/BoardFile";

interface UseFileSpotIncentiveProps {
  entityRecord: EntityRecord;
  project: ISwivuProject | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editorHandler: any;
  onSuccess?: () => void;
}

/**
 * Custom hook to handle spot incentive operations for files
 * Provides logic for checking if a file is eligible for spot incentive
 * and handler for opening the spot incentive editor
 */
export const useFileSpotIncentive = ({
  entityRecord,
  project,
  editorHandler,
  onSuccess
}: UseFileSpotIncentiveProps) => {
  /**
   * Check if "Adjust Points" should be shown for a file
   * Both BoardFiles and CompanyCam images are eligible
   */
  const shouldShowAdjustPoints = useCallback(
    (file: FormattedFile | null) => {
      if (!file) return false;

      const isImage = isImageFile(file);

      const isTaskRelated = file.isCCImage
        ? entityRecord?.isTask === true
        : Boolean(file.record?.taskId);

      return isImage && isTaskRelated;
    },
    [entityRecord]
  );

  /**
   * Handler for adjusting points on a file
   */
  const handleAdjustPoints = useCallback(
    async (file: FormattedFile, isCreatingUnreleasedRecord?: boolean, onReviewClick?: (newFile?: BoardFile) => void) => {
      if (!file || !project) return;

      try {
        const fileData = await prepareFileForSpotIncentive(file);

        if (!fileData) {
          toast.error("Unable to use this file for spot point");
          return;
        }

        // Get the task associated with the file
        let task = null;
        if (file.isCCImage) {
          task = entityRecord?.isTask ? entityRecord : null;
        } else {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          task = file.record?.taskId ? (project as any).extraTaskStore.getById(file.record.taskId) : null;
        }

        // Calculate initial values based on file uploaded date
        let initialName = "";
        let initialStartDate: string | undefined;
        let initialEndDate: string | undefined;
        let initialResourceIds: number[] = [];

        // Get uploaded date from file
        const uploadedAt = file.record?.originalData?.created_at || file.createdAt || file.lastUpdatedAt;
        if (uploadedAt && task?.id) {
          const uploadedDate = new Date(uploadedAt);
          // Set to start of day
          uploadedDate.setHours(0, 0, 0, 0);

          // Use uploaded date as both start and end date
          initialStartDate = uploadedDate.toISOString();
          initialEndDate = uploadedDate.toISOString();

          // Get eligible resources for the uploaded date
          try {
            const events = getEligibleResourceForSpotIncentive({
              taskId: task.id,
              project,
              startDate: uploadedDate,
              endDate: DateHelper.add(uploadedDate, 1, "day")
            });
            initialResourceIds = events.map(({ record }) => Number(record.resourceId));
          } catch (error) {
            console.error("Error getting eligible resources:", error);
          }
        }

        // Generate initial name
        if (file.id && entityRecord?.name) {
          initialName = `${entityRecord.name} - ${file.name}`;
        }

        openAdjustPointsEditorWithGuard({
          project,
          editorHandler,
          record: task,
          otherParams: {
            initialFileId: fileData.fileId,
            initialFileData: fileData.fileId ? undefined : fileData,
            isCreatingUnreleasedRecord,
            onReviewClick,
            initialName,
            initialStartDate,
            initialEndDate,
            initialResourceIds
          }
        });

        onSuccess?.();
      } catch (error) {
        console.error("Error creating spot point:", error);
        toast.error("Failed to create spot point");
      }
    },
    [project, editorHandler, entityRecord, onSuccess]
  );

  return {
    shouldShowAdjustPoints,
    handleAdjustPoints
  };
};
